/**
 * Project-wide content search — pure logic (no DOM), unit-tested.
 *
 * Literal (non-regex) substring search over the raw source of every file and
 * component in the project. Returns per-file results with line-level context
 * so the UI can render "file → matching lines" and jump to an exact offset.
 */

import { parseEmbedDirective } from '../../shared/index.js';

export interface ProjectSearchInput {
  path: string;
  content: string;
}

export interface ProjectSearchMatch {
  /** 1-based line number */
  line: number;
  /** Full text of the matching line (untrimmed) */
  lineText: string;
  /** 0-based column of the match within the line */
  column: number;
  /** Absolute offset of the match within the file content */
  offset: number;
  /** Match length in characters */
  length: number;
}

export interface ProjectSearchFileResult {
  path: string;
  matches: ProjectSearchMatch[];
  /** True when this file had more matches than were collected */
  truncated: boolean;
}

export interface ProjectSearchOptions {
  caseSensitive?: boolean;
  /** Max matches collected per file (default 50) */
  maxPerFile?: number;
  /** Max matches collected across the whole project (default 500) */
  maxTotal?: number;
}

export interface ProjectSearchResult {
  files: ProjectSearchFileResult[];
  totalMatches: number;
  /** True when the overall cap stopped the search early */
  capped: boolean;
}

/**
 * Search every file for `query`. Files are returned with README first, then
 * regular files, then components, each group sorted by path — matching the
 * order of the file browser.
 */
export function searchProject(
  inputs: ProjectSearchInput[],
  query: string,
  opts: ProjectSearchOptions = {}
): ProjectSearchResult {
  const maxPerFile = opts.maxPerFile ?? 50;
  const maxTotal = opts.maxTotal ?? 500;
  const result: ProjectSearchResult = { files: [], totalMatches: 0, capped: false };
  if (!query) return result;

  // Regex (escaped) rather than toLowerCase+indexOf so offsets stay exact
  // even for characters whose lowercase form has a different length
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sorted = [...inputs].sort((a, b) => sortKey(a.path).localeCompare(sortKey(b.path)));

  for (const input of sorted) {
    if (result.totalMatches >= maxTotal) {
      result.capped = true;
      break;
    }
    const content = input.content;
    const regex = new RegExp(escaped, opts.caseSensitive ? 'g' : 'gi');
    const matches: ProjectSearchMatch[] = [];
    let truncated = false;

    // Line index is built lazily, only for files that actually match
    let lineStarts: number[] | null = null;

    let m: RegExpExecArray | null;
    while ((m = regex.exec(content)) !== null) {
      const idx = m.index;
      if (matches.length >= maxPerFile || result.totalMatches >= maxTotal) {
        truncated = true;
        if (result.totalMatches >= maxTotal) result.capped = true;
        break;
      }
      lineStarts ??= buildLineStarts(content);
      const lineIdx = findLine(lineStarts, idx);
      const start = lineStarts[lineIdx];
      const end = lineIdx + 1 < lineStarts.length ? lineStarts[lineIdx + 1] - 1 : content.length;
      matches.push({
        line: lineIdx + 1,
        lineText: content.slice(start, end).replace(/\r$/, ''),
        column: idx - start,
        offset: idx,
        length: m[0].length,
      });
      result.totalMatches++;
    }

    if (matches.length > 0) {
      result.files.push({ path: input.path, matches, truncated });
    }
  }

  return result;
}

/**
 * Find every `<!-- embed: ... -->` directive (in files and other components)
 * that resolves to the component `componentName` (its path relative to
 * `_components/`, e.g. `docker.md` or `infra/docker.md`). Resolution mirrors
 * weave: exact path, or the embed's basename for top-level components.
 * Returned in the same shape as `searchProject` so the same UI renders it.
 */
export function findComponentUsages(
  inputs: ProjectSearchInput[],
  componentName: string
): ProjectSearchResult {
  const name = componentName.replace(/^_components\//, '');
  const self = `_components/${name}`;
  const result: ProjectSearchResult = { files: [], totalMatches: 0, capped: false };
  const sorted = [...inputs].sort((a, b) => sortKey(a.path).localeCompare(sortKey(b.path)));
  const componentNames = new Set(
    inputs.filter(i => i.path.startsWith('_components/')).map(i => i.path.slice('_components/'.length))
  );
  // Same lookup order as weave's componentResolver: exact, then basename
  const resolve = (ref: string): string | null => {
    if (componentNames.has(ref)) return ref;
    const base = ref.split('/').pop() ?? '';
    return componentNames.has(base) ? base : null;
  };

  for (const input of sorted) {
    if (input.path === self || !input.content.includes('embed')) continue;
    const matches: ProjectSearchMatch[] = [];
    let offset = 0;
    const lines = input.content.split('\n');
    lines.forEach((rawLine, i) => {
      const line = rawLine.replace(/\r$/, '');
      const directive = parseEmbedDirective(line, i + 1);
      if (directive) {
        const ref = directive.component.replace(/^\.\//, '').replace(/^_components\//, '');
        if (resolve(ref) === name) {
          const column = line.indexOf(directive.component);
          matches.push({
            line: i + 1,
            lineText: line,
            column,
            offset: offset + column,
            length: directive.component.length,
          });
        }
      }
      offset += rawLine.length + 1;
    });
    if (matches.length > 0) {
      result.files.push({ path: input.path, matches, truncated: false });
      result.totalMatches += matches.length;
    }
  }
  return result;
}

/**
 * Trim a long line to a window around the match so the hit stays visible.
 * Returns the three parts to render (before / match / after).
 */
export function snippetAround(
  match: ProjectSearchMatch,
  radius = 60
): { before: string; text: string; after: string } {
  const line = match.lineText;
  const matchEnd = match.column + match.length;
  const from = Math.max(0, match.column - radius);
  const to = Math.min(line.length, matchEnd + radius);
  const before = line.slice(from, match.column);
  return {
    // Drop leading indentation when the window starts at the line start
    before: from === 0 ? before.trimStart() : '…' + before,
    text: line.slice(match.column, matchEnd),
    after: line.slice(matchEnd, to) + (to < line.length ? '…' : ''),
  };
}

function sortKey(path: string): string {
  if (path === 'README.src.md') return '0';
  if (path.startsWith('_components/')) return '2' + path.toLowerCase();
  return '1' + path.toLowerCase();
}

function buildLineStarts(text: string): number[] {
  const starts = [0];
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 10) starts.push(i + 1);
  }
  return starts;
}

function findLine(starts: number[], offset: number): number {
  let lo = 0;
  let hi = starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (starts[mid] <= offset) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}
